import { Link } from 'react-router-dom';
import Countdown from '../components/Countdown';
import { projets as staticProjets, careers, stats, temoignages, domaines as staticDomaines } from '../data/siteData';
import { hero, alumni as alumniPhotos, partnersLight, partnersDark, sections } from '../assets';
import { useEffect, useState } from 'react';
import { getPublicSettings, getPublicPartenaires, getPublicProjets } from '../services/contentApi';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import './Home.css';

const DEFAULT_ABOUT_PILIERS = [
  'Formations intensives, certifiantes, adaptées au marché local',
  'Accès pour tous — financement institutionnel ou fonds propres',
  "Partenariats bailleurs pour financer l'insertion des jeunes",
  "85% d'insertion professionnelle sur nos cohortes",
];

const STATUS_LABEL = { en_cours: 'En cours', termine: 'Complété' };

function useTheme() {
  const [dark, setDark] = useState(() =>
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setDark(document.documentElement.getAttribute('data-theme') === 'dark');
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

export default function Home() {
  const isDark = useTheme();
  const staticPartners = isDark ? partnersDark : partnersLight;

  const { sections: cmsSections, page } = useCmsPage('accueil');
  useSeoMeta(page, { title: 'Tech Talent Accelerator — Accueil', desc: "Accélérateur de talents tech en Afrique de l'Ouest. Formations IA, fullstack, data." });
  const getSec = type => cmsSections.find(s => s.type === type)?.content || {};
  const cmsHero        = getSec('hero');
  const cmsForprode    = getSec('forprode_banner');
  const cmsDomaines    = getSec('domaines_home');
  const cmsCareers     = getSec('careers_home');
  const cmsBailleurs   = getSec('bailleurs_home');
  const cmsProjets     = getSec('projets_home');
  const cmsTemo        = getSec('temoignages_home');
  const cmsAbout       = getSec('about_home');
  const cmsCta         = getSec('cta_dark');

  const aboutPiliers = (cmsAbout.piliers || []).length ? cmsAbout.piliers : DEFAULT_ABOUT_PILIERS;

  const [cmsSettings, setCmsSettings]     = useState(null);
  const [cmsPartenaires, setCmsPartenaires] = useState(null);
  const [projets, setProjets]              = useState(staticProjets);
  const domaines = (cmsDomaines.domains && cmsDomaines.domains.length) ? cmsDomaines.domains : staticDomaines;

  useEffect(() => { getPublicSettings().then(d => { if (d) setCmsSettings(d); }); }, []);
  useEffect(() => { getPublicPartenaires().then(d => { if (d && d.length) setCmsPartenaires(d); }); }, []);

  useEffect(() => {
    getPublicProjets().then(data => {
      if (!data || !data.length) return;
      setProjets(staticProjets.map(sp => {
        const live = data.find(a => a.id === sp.id);
        if (!live) return sp;
        return {
          ...sp,
          title:       live.title       || sp.title,
          description: live.description || sp.description,
          partner:     live.partner     || sp.partner,
          year:        live.year        || sp.year,
          status:      STATUS_LABEL[live.status] || sp.status,
          imageUrl:    live.image_url   || null,
          lienExterne: live.lien_externe || null,
        };
      }));
    });
  }, []);


  const forprode = staticProjets.find(p => p.id === 'forprode-40-ia');
  const countdownDate = cmsSettings?.forprode_date || forprode?.candidatureDate;
  const countdownMode = cmsSettings?.forprode_mode || forprode?.candidatureMode;

  // Partenaires : utilise CMS seulement si des logos existent vraiment
  const activePartenaires = cmsPartenaires?.filter(p => p.actif !== 0);
  const cmsHasLogos = activePartenaires?.some(p => p.logo_url);
  const partners = cmsHasLogos
    ? activePartenaires.filter(p => p.logo_url).map(p => ({
        src: isDark && p.logo_dark_url ? p.logo_dark_url : p.logo_url,
        alt: p.nom,
      }))
    : staticPartners;
  return (
    <main className="home">

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero__bg-orb" data-parallax="0.45" />
        <div className="hero__bg-orb2" data-parallax="-0.30" />
        <div className="hero__grid" />
        <div className="hero__left">
          <div className="hero__eyebrow animate-fade-up">
            <span className="hero__eyebrow-dot" />
            <span>{cmsHero.eyebrow || "Bootcamps certifiants · Afrique de l'Ouest"}</span>
          </div>
          <h1 className="hero__h1 animate-fade-up-d1">
            {cmsHero.title || 'Lancez votre'}<br />
            <span className="grad-text">{cmsHero.title_grad || 'carrière tech'}</span>
            {cmsHero.title_line3 ? <><br />{cmsHero.title_line3}</> : (!cmsHero.title && <><br />en 12 semaines</>)}
          </h1>
          <p className="hero__sub animate-fade-up-d2">
            {cmsHero.subtitle || "Des formations intensives pour tous — sur financement institutionnel ou accessibles sur fonds propres. Du débutant au professionnel, nous accélérons votre transformation."}
          </p>
          <div className="hero__ctas animate-fade-up-d3">
            <Link to="/candidater" className="btn-primary">{cmsHero.cta1 || 'Candidater à un programme'} →</Link>
            <Link to="/bailleurs" className="btn-outline">{cmsHero.cta2 || 'Pour les bailleurs'}</Link>
          </div>
          <div className="hero__stats animate-fade-up-d3">
            {stats.map((s, i) => (
              <div key={s.label} className="hero__stat-group">
                {i > 0 && <div className="hero__stat-divider" />}
                <div className="hero__stat">
                  <span className="hero__stat-num grad-text">{s.value}</span>
                  <span className="hero__stat-label">{s.label}</span>
                </div>
              </div>
            ))}
          </div>


        </div>

        <div className="hero__right animate-slide-right">
          <div className="hero__card-float hero__card-float--left">
            <div className="hero__card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="2" strokeLinecap="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div>
              <div className="hero__card-title">Certification internationale</div>
              <div className="hero__card-sub">Reconnue mondialement</div>
            </div>
          </div>
          <div className="hero__card-float hero__card-float--right">
            <div className="hero__card-icon hero__card-icon--dark">
              <svg viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="2" strokeLinecap="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              </svg>
            </div>
            <div>
              <div className="hero__card-title--dark">Promos actives</div>
              <div className="hero__card-sub--dark">2 cohortes / an</div>
            </div>
          </div>
          <div className="hero__card-float hero__card-float--bottom">
            <div className="hero__card-icon" style={{ background: 'rgba(139,197,63,0.1)' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="var(--light)" strokeWidth="2" strokeLinecap="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
              </svg>
            </div>
            <div>
              <div className="hero__card-title--light">Taux d'insertion</div>
              <div className="hero__card-sub--light" style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, color:'var(--mid)' }}>85% en 3 mois</div>
            </div>
          </div>

          {/* IMAGE + PROFILE-SHARE dans le même conteneur relatif */}
          <div className="hero__img-wrap">
            <img src={hero.main} alt="Apprenants TTA" className="hero__img" />
            <div className="hero__profile-share">
              <div className="hero__profile-avatars">
                {alumniPhotos.slice(0, 5).map((src, i) => (
                  <img key={i} src={src} alt={`Alumni ${i+1}`} className="hero__profile-avatar" />
                ))}
              </div>
              <div className="hero__profile-text">
                <span className="hero__profile-title">+500 talents formés</span>
                <span className="hero__profile-sub">85% insérés en moins de 3 mois</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNERS STRIP ── */}
      <div className="partners-strip">
        <span className="partners-strip__label">Ils nous font confiance</span>
        <div className="partners-strip__logos">
          {partners.map(p => (
            <img key={p.alt} src={p.src} alt={p.alt} className="partners-strip__logo-img" />
          ))}
        </div>
      </div>

      {/* ── FORPRODE BANNER ── */}
      {forprode && (
        <section className="forprode-banner">
          <div className="forprode-banner__bg" />
          <div className="forprode-banner__inner">
            <div className="forprode-banner__left">
              <div className="forprode-banner__badge">
                <span className="forprode-banner__badge-dot" />
                <span>{cmsForprode.badge || 'Programme en cours · 2025'}</span>
              </div>
              <h2 className="forprode-banner__title">{cmsForprode.title || 'FORPRODE'} <span className="grad-text">{cmsForprode.title_grad || '40 IA'}</span></h2>
              <p className="forprode-banner__desc">
                {cmsForprode.description || "Formation de 50 jeunes aux métiers de l'intelligence artificielle, du développement fullstack et de la data."}
              </p>
              <Link to="/projets/forprode-40-ia" className="forprode-banner__btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                Découvrir le programme
              </Link>
            </div>
            <div className="forprode-banner__right">
              <Countdown targetDate={countdownDate} mode={countdownMode} />
            </div>
          </div>
        </section>
      )}

      {/* ── DOMAINES D'INTERVENTION ── */}
      <section className="section">
        <div className="section-eyebrow">{cmsDomaines.eyebrow || "Nos domaines d'intervention"}</div>
        <h2 className="section-title">{cmsDomaines.title || 'Les domaines tech'}<br />{cmsDomaines.title_line2 || 'de notre écosystème'}</h2>
        <p className="section-subtitle">{cmsDomaines.subtitle || "De l'intelligence artificielle au développement web, TTA couvre les compétences numériques les plus demandées par le marché africain."}</p>
        <div className="domaines-grid">
          {domaines.map(d => (
            <div key={d.id} className="domaine-card">
              <div className="domaine-card__icon">
                {d.icon === 'brain'    && <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M2 12h4M18 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>}
                {d.icon === 'chart'    && <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
                {d.icon === 'code'     && <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>}
                {d.icon === 'terminal' && <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>}
                {d.icon === 'cloud'    && <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>}
                {d.icon === 'shield'   && <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
              </div>
              <div className="domaine-card__title">{d.title}</div>
              <div className="domaine-card__desc">{d.desc}</div>
              <div className="domaine-card__metiers">
                {(d.metiers || []).map(m => <span key={m} className="domaine-card__metier">{m}</span>)}
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign:'center', marginTop:'32px' }}>
          <Link to="/carrieres" className="btn-outline">{cmsDomaines.cta_label || 'Explorer les métiers tech →'}</Link>
        </div>
      </section>

      {/* ── CARRIÈRES ── */}
      <section className="section section-alt">
        <div className="careers-grid">
          <div className="careers-intro">
            <div className="section-eyebrow">{cmsCareers.eyebrow || "Opportunités de carrière"}</div>
            <h2 className="section-title" style={{ fontSize:'32px' }}>{cmsCareers.title || "La tech & l'IA ouvrent"}<br />{cmsCareers.title_line2 || 'des portes infinies'}</h2>
            <p className="section-subtitle" style={{ fontSize:'16px', marginBottom:'20px' }}>{cmsCareers.subtitle || "Le marché numérique africain recrute massivement. Développeurs, data analysts, experts IA — les salaires et opportunités ne cessent de croître."}</p>
            <div className="careers-stats-grid">
              {(cmsCareers.stats || [{ val:'3x', lbl:"Croissance emplois tech en Afrique d'ici 2030" },{ val:'40%', lbl:'Hausse des salaires tech en 5 ans' },{ val:'85%', lbl:'De nos diplômés trouvent un emploi en 3 mois' },{ val:'500k+', lbl:'Postes tech non pourvus en Afrique' }]).map(s => (
                <div key={s.val} className="careers-stat-card">
                  <div className="careers-stat-val grad-text">{s.val}</div>
                  <div className="careers-stat-lbl">{s.lbl}</div>
                </div>
              ))}
            </div>
            <div className="careers-cta-box">
              <div className="careers-cta-box__title">{cmsCareers.cta_title || 'Recrutez nos talents formés'}</div>
              <div className="careers-cta-box__desc">{cmsCareers.cta_desc || "Accédez à un vivier de profils qualifiés, certifiés et prêts à l'emploi."}</div>
              <div className="careers-cta-box__btns">
                <Link to="/carrieres" className="btn-primary" style={{ fontSize:'13px', padding:'9px 18px' }}>Explorer les carrières →</Link>
                <Link to="/carrieres#entreprises" className="btn-outline" style={{ fontSize:'13px', padding:'9px 16px' }}>Espace entreprises</Link>
              </div>
            </div>
          </div>
          <div className="careers-cards">
            {careers.map(c => (
              <Link to="/carrieres" key={c.id} className="career-card">
                <div className="career-card__ico">
                  {c.icon === 'code' && <svg viewBox="0 0 24 24" fill="none" stroke="var(--deep)" strokeWidth="1.8" strokeLinecap="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>}
                  {c.icon === 'brain' && <svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M2 12h4M18 12h4"/></svg>}
                  {c.icon === 'chart' && <svg viewBox="0 0 24 24" fill="none" stroke="var(--light)" strokeWidth="1.8" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>}
                  {c.icon === 'users' && <svg viewBox="0 0 24 24" fill="none" stroke="var(--deep)" strokeWidth="1.8" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>}
                </div>
                <div style={{ flex:1 }}>
                  <div className="career-card__title">{c.title}</div>
                  <div className="career-card__desc">{c.description}</div>
                  <div className="career-card__salary">{c.salary}</div>
                </div>
                <div className="career-card__growth">{c.growth}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── BAILLEURS ── */}
      <section className="section">
        <div className="bailleurs-layout">
          <div>
            <div className="section-eyebrow">{cmsBailleurs.eyebrow || 'Bailleurs & Partenaires'}</div>
            <h2 className="section-title" style={{ fontSize:'32px' }}>{cmsBailleurs.title || "Financez l'avenir"}<br />{cmsBailleurs.title_line2 || 'numérique africain'}</h2>
            <p className="section-subtitle" style={{ fontSize:'16px' }}>{cmsBailleurs.subtitle || "TTA est conçu pour répondre aux exigences des bailleurs institutionnels. Transparence, impact mesurable, insertion réelle."}</p>
            <Link to="/bailleurs" className="btn-primary">{cmsBailleurs.cta_label || 'Devenir partenaire bailleur'} →</Link>
          </div>
          <div className="bailleurs-cards">
            {[
              { title:'Impact mesurable & rapports complets', desc:"Tableaux de bord, taux d'insertion, suivi des apprenants. Reporting institutionnel complet." },
              { title:'Certification internationale incluse', desc:'Programmes certifiants, reconnus mondialement et adaptés au contexte africain.' },
              { title:'Programmes co-construits', desc:"Conception sur-mesure selon vos objectifs. Votre vision, notre exécution." },
            ].map(b => (
              <div key={b.title} className="bailleur-card">
                <div className="bailleur-card__icon"><svg viewBox="0 0 24 24" fill="none" stroke="var(--mid)" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg></div>
                <div><div className="bailleur-card__title">{b.title}</div><div className="bailleur-card__desc">{b.desc}</div></div>
              </div>
            ))}
            <div className="bailleur-card bailleur-card--dark">
              <div className="bailleur-card__icon"><svg viewBox="0 0 24 24" fill="none" stroke="#8BC53F" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg></div>
              <div><div className="bailleur-card__title--dark">Références de terrain</div><div className="bailleur-card__desc--dark">ATOS Digital Academy · FORPRODE 40 IA · Côte d'Ivoire, Sénégal</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PROJETS ── */}
      <section className="section section-alt">
        <div className="section-eyebrow">{cmsProjets.eyebrow || 'Nos projets'}</div>
        <h2 className="section-title">{cmsProjets.title || 'Ce que nous avons réalisé'}</h2>
        <p className="section-subtitle">{cmsProjets.subtitle || 'De la conception au déploiement, des programmes à impact réel.'}</p>
        <div className="projets-grid">
          {projets.map(p => (
            <Link to={`/projets/${p.slug}`} key={p.id} className="projet-card">
              <div className="projet-card__band" />
              <div className="projet-card__body">
                <span className={`projet-card__badge ${p.status === 'Complété' ? 'projet-card__badge--done' : ''}`}>{p.status} · {p.year}</span>
                <div className="projet-card__title">{p.title}</div>
                <div className="projet-card__desc">{p.description}</div>
                <div className="projet-card__footer">
                  <div className="projet-card__partner">Partenaire : <strong>{p.partner}</strong></div>
                  <span className="projet-card__link">Voir le projet →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── TÉMOIGNAGES ── */}
      <section className="section">
        <div className="section-eyebrow">{cmsTemo.eyebrow || 'Ils nous font confiance'}</div>
        <h2 className="section-title">{cmsTemo.title || 'Ce que disent nos alumni'}</h2>
        <div className="temoignages-grid">
          {temoignages.map((t, i) => (
            <div key={t.id} className="temoignage-card">
              <span className="temoignage-card__formation">{t.formation}</span>
              <div className="temoignage-card__quote">"{t.quote}"</div>
              <div className="temoignage-card__footer">
                <div className="temoignage-card__avatar">
                  <img src={alumniPhotos[i]} alt={t.name} />
                </div>
                <div>
                  <div className="temoignage-card__name">{t.name}</div>
                  <div className="temoignage-card__role">{t.role} · {t.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── À PROPOS ── */}
      <section className="section section-alt">
        <div className="about-layout">
          <div className="about-visual opt-a">
            <div className="line-top-behind">Catalyseur de la</div>
            <div className="line-front">
              <span className="text-bright">transfo</span><span className="text-on-face">rmati</span><span className="text-bright">on tech</span>
            </div>
            <div className="line-behind">en Afrique de l'Ouest.</div>
            <img className="about-img" src={sections.image003} alt="À propos TTA" />
          </div>
          <div className="about-content">
            <div className="section-eyebrow">{cmsAbout.eyebrow || 'À propos de TTA'}</div>
            <h2 className="section-title" style={{ fontSize:'30px' }}>{cmsAbout.h2 || 'Une mission claire :'}<br />{cmsAbout.h2_line2 || 'former les talents'}<br />{cmsAbout.h2_line3 || 'de demain'}</h2>
            <p style={{ fontSize:'16px', color:'var(--text-secondary)', lineHeight:1.7, marginBottom:'8px' }}>
              {cmsAbout.subtitle || "Tech Talent Accelerator propose des bootcamps certifiants pour accélérer les carrières tech, avec une expertise reconnue en insertion professionnelle en Afrique de l'Ouest."}
            </p>
            <div className="about-piliers">
              {aboutPiliers.map((p, i) => (
                <div key={i} className="about-pilier">
                  <span className="about-pilier__num grad-text">0{i+1}</span>
                  <span className="about-pilier__text">{typeof p === 'string' ? p : p.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="cta-section">
        <div className="cta-section__bg" />
        <h2 className="cta-section__title">{cmsCta.h2 || 'Rejoignez la prochaine'}<br /><span className="grad-text">{cmsCta.h2_grad || 'cohorte TTA'}</span></h2>
        <p className="cta-section__sub">{cmsCta.p || "Candidatez dès aujourd'hui — ou contactez-nous pour co-construire votre programme."}</p>
        <div className="cta-section__btns">
          <Link to="/candidater" className="btn-primary" style={{ fontSize:'16px', padding:'14px 30px' }}>{cmsCta.cta1_label || 'Candidater maintenant'} →</Link>
          <Link to="/contact" style={{ fontFamily:"'Sora',sans-serif", fontSize:'16px', fontWeight:500, padding:'14px 24px', background:'transparent', border:'1px solid rgba(255,255,255,.2)', color:'rgba(255,255,255,.8)', borderRadius:'9px' }}>{cmsCta.cta2_label || "Contacter l'équipe"}</Link>
        </div>
      </section>

    </main>
  );
}
